import {
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import type { CondicaoPagto, Parcela } from '@azoupvendas/shared';
import { ParcelasTable } from '../ParcelasTable';

// AIDEV-NOTE: Helpers de formatacao consistentes com o restante do modulo
const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
function formatCurrency(value: number): string {
  return brlFormatter.format(value);
}

interface Step4PagamentoManagedProps {
  descontoGeral: number;
  condicaoId: number | null;
  onCondicaoChange: (id: number | null) => void;
  condicoes: CondicaoPagto[];
  parcelas: Parcela[];
  totalComCondicao: number;
  totalProdutos: number;
  observacao: string;
  onObservacaoChange: (v: string) => void;
  origem: string;
  onOrigemChange: (v: string) => void;
  origens: Array<{ codigo: number; descricao: string }>;
  // AIDEV-NOTE: Restricoes de condicao de pagamento vindas do JWT do usuario
  restricoes?: Array<{
    condicao_pagto_id: number;
    ativo: boolean;
    valor_minimo_pedido: number | null;
  }>;
  // AIDEV-NOTE: Controla visibilidade do select de origem do pedido
  podeAlterarOrigem?: boolean;
  tipoMovimentoReceberId: number | null;
  onTipoMovimentoReceberChange: (id: number | null) => void;
  tiposMovimentoReceber: Array<{ codigo: number; descricao: string }>;
  // AIDEV-NOTE: Se false, vendedor usa o padrão e o select fica desabilitado (politica do admin)
  podeAlterarTipoMovimento?: boolean;
}

// AIDEV-NOTE: Step 4 "Managed" — condicao de pagamento, observacao e origem do pedido foram
// movidos para ca do Step3 (Carrinho) para separar revisao de itens da configuracao de pagamento.
// Layout scrollavel para acomodar todos os campos + tabela de parcelas.

export function Step4PagamentoManaged({
  condicaoId,
  onCondicaoChange,
  condicoes,
  parcelas,
  totalComCondicao,
  totalProdutos,
  observacao,
  onObservacaoChange,
  origem,
  onOrigemChange,
  origens,
  tipoMovimentoReceberId,
  onTipoMovimentoReceberChange,
  tiposMovimentoReceber,
  restricoes,
  podeAlterarOrigem,
  podeAlterarTipoMovimento = true
}: Step4PagamentoManagedProps) {
  const condicaoSelecionada = condicoes.find((c) => c.id === condicaoId) ?? null;

  // AIDEV-NOTE: Filtra condicoes baseado em restricoes_condicao_pagto_jwt do JWT
  // Condicoes nao listadas em restricoes = permitidas (blacklist approach)
  const condicoesVisiveis = condicoes.filter((c) => {
    const restricao = restricoes?.find((r) => r.condicao_pagto_id === c.id);
    return !restricao || restricao.ativo;
  });

  const isDisabledByValorMinimo = (
    condicaoId: number
  ): { disabled: boolean; minimo: number | null } => {
    const restricao = restricoes?.find((r) => r.condicao_pagto_id === condicaoId);
    if (!restricao?.valor_minimo_pedido) return { disabled: false, minimo: null };
    return {
      disabled: totalProdutos < restricao.valor_minimo_pedido,
      minimo: restricao.valor_minimo_pedido
    };
  };

  return (
    // AIDEV-NOTE: flex:1 + minHeight:0 e a forma correta de preencher o slot do wizard em flex column.
    // height:'100%' nao funciona confiavelmente em flex child — o conteudo expandia a tela em vez de
    // rolar internamente. overscrollBehavior:contain isola o scroll do step no mobile.
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        p: { xs: 2, sm: 3 },
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <Typography variant="h5" sx={{ mb: 3 }}>
        Pagamento
      </Typography>

      {/* Tipo de Movimento (Receber) — só exibe se há opções E vendedor pode alterar */}
      {tiposMovimentoReceber.length > 0 && podeAlterarTipoMovimento && (
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel id="tipo-movimento-receber-label">Tipo de Movimento (Receber)</InputLabel>
          <Select
            labelId="tipo-movimento-receber-label"
            value={tipoMovimentoReceberId ?? ''}
            label="Tipo de Movimento (Receber)"
            onChange={(e) => {
              const v = e.target.value;
              onTipoMovimentoReceberChange(v === '' ? null : Number(v));
            }}
          >
            <MenuItem value="">Padrao do sistema</MenuItem>
            {tiposMovimentoReceber.map((t) => (
              <MenuItem key={t.codigo} value={t.codigo}>
                {t.descricao}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Condicao de Pagamento */}
      <FormControl fullWidth size="small" sx={{ mb: 3 }}>
        <InputLabel id="condicao-pagto-label">Condicao de Pagamento *</InputLabel>
        <Select
          labelId="condicao-pagto-label"
          value={condicaoId ?? ''}
          label="Condicao de Pagamento *"
          onChange={(e) => {
            const v = e.target.value;
            onCondicaoChange(v === '' ? null : Number(v));
          }}
        >
          <MenuItem value="">Selecione uma condicao...</MenuItem>
          {condicoesVisiveis.map((c) => {
            const { disabled, minimo } = isDisabledByValorMinimo(c.id);
            return (
              <MenuItem key={c.id} value={c.id} disabled={disabled}>
                {c.descricao}
                {c.qtde_parcelas > 1 && ` (${c.qtde_parcelas}x)`}
                {c.acrescimo > 0 && ` +${c.acrescimo}%`}
                {c.desconto > 0 && ` -${c.desconto}%`}
                {disabled && minimo && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                    (min.{' '}
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                      minimo
                    )}
                    )
                  </Typography>
                )}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {/* Resumo de totais com condicao */}
      {condicaoSelecionada ? (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total produtos
            </Typography>
            <Typography variant="body2">{formatCurrency(totalProdutos)}</Typography>
          </Box>
          {condicaoSelecionada.acrescimo > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="warning.main">
                Acrescimo ({condicaoSelecionada.acrescimo}%)
              </Typography>
              <Typography variant="body2" color="warning.main">
                + {formatCurrency(totalProdutos * (condicaoSelecionada.acrescimo / 100))}
              </Typography>
            </Box>
          )}
          {condicaoSelecionada.desconto > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="success.main">
                Desconto condicao ({condicaoSelecionada.desconto}%)
              </Typography>
              <Typography variant="body2" color="success.main">
                -{' '}
                {formatCurrency(
                  totalProdutos *
                    (1 + condicaoSelecionada.acrescimo / 100) *
                    (condicaoSelecionada.desconto / 100)
                )}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              pt: 1,
              borderTop: '2px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Total Final
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {formatCurrency(totalComCondicao)}
            </Typography>
          </Box>

          {/* Tabela de parcelas */}
          {parcelas.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <ParcelasTable parcelas={parcelas} />
            </Box>
          )}
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Selecione uma condicao de pagamento para ver o total e as parcelas.
        </Alert>
      )}

      {/* Origem do pedido — oculto se usuario nao pode alterar origem */}
      {podeAlterarOrigem !== false && (
        <FormControl size="small" sx={{ mb: 3, minWidth: 220 }}>
          <InputLabel id="origem-pedido-label">Origem do Pedido</InputLabel>
          <Select
            labelId="origem-pedido-label"
            value={origem}
            label="Origem do Pedido"
            onChange={(e) => onOrigemChange(e.target.value)}
          >
            <MenuItem value="">Selecione uma origem...</MenuItem>
            {origens.map((o) => (
              <MenuItem key={o.codigo} value={o.descricao}>
                {o.descricao}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Observacao */}
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={6}
        label="Observacao"
        value={observacao}
        onChange={(e) => onObservacaoChange(e.target.value)}
        inputProps={{ maxLength: 500 }}
        helperText={`${observacao.length}/500`}
      />
    </Box>
  );
}
